/**
 * useInviteCodes — Hook for invite code CRUD operations
 */

import { useState, useCallback, useEffect } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";
import logger from "services/logger";

export default function useInviteCodes({ autoFetch = false } = {}) {
  const { get, post, del } = useApi();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchInvites = useCallback(
    async (includeUsed = false) => {
      setLoading(true);
      setError(null);
      try {
        const url = includeUsed
          ? `${endpoints.inviteCodes.base}?includeUsed=true`
          : endpoints.inviteCodes.base;
        const data = await get(url);
        setInvites(data.invites || []);
        return data.invites;
      } catch (err) {
        logger.error("[useInviteCodes]", "Failed to fetch invites:", err);
        setError(err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [get]
  );

  const createInvite = useCallback(
    async (inviteData) => {
      try {
        const data = await post(endpoints.inviteCodes.base, inviteData);
        setInvites((prev) => [data.invite, ...prev]);
        return data.invite;
      } catch (err) {
        logger.error("[useInviteCodes]", "Failed to create invite:", err);
        throw err;
      }
    },
    [post]
  );

  const revokeInvite = useCallback(
    async (id) => {
      try {
        await del(endpoints.inviteCodes.byId(id));
        setInvites((prev) => prev.filter((inv) => inv.id !== id));
      } catch (err) {
        logger.error("[useInviteCodes]", "Failed to revoke invite:", err);
        throw err;
      }
    },
    [del]
  );

  const resendEmail = useCallback(
    async (id) => {
      try {
        await post(endpoints.inviteCodes.resend(id));
        return true;
      } catch (err) {
        logger.error("[useInviteCodes]", "Failed to resend invite email:", err);
        throw err;
      }
    },
    [post]
  );

  const validateCode = useCallback(
    async (code, email) => {
      try {
        const data = await post(endpoints.inviteCodes.validate, { code, email });
        return data;
      } catch (err) {
        logger.error("[useInviteCodes]", "Failed to validate invite code:", err);
        throw err;
      }
    },
    [post]
  );

  useEffect(() => {
    if (autoFetch) fetchInvites();
  }, [autoFetch, fetchInvites]);

  return {
    invites,
    loading,
    error,
    fetchInvites,
    createInvite,
    revokeInvite,
    resendEmail,
    validateCode,
  };
}
